import PostStockButton from "../../components/PostStockButton";
import EntityLabel from "../../components/EntityLabel";
import { useWarehouse } from "../../warehouse/WarehouseContext";
import { useEffect, useState } from "react";
import ManagementPageHTML, {
  IdentityCell,
  StatusBadge,
} from "../../components/ManagementPage/ManagementPageHTML";
import type {
  InventoryCount,
  InventoryCountItem,
} from "../../models/InventoryCount";
import { Status } from "../../models/Status";
import { InventoryCountService } from "../../services/InventoryCountService";
import { InventoryService } from "../../services/InventoryService";
import {
  useInventoryLookups,
  loadAllLookupPages,
} from "../../hooks/useInventoryLookups";
import {
  CountLines,
  validateInventoryLines,
} from "../../components/InventoryLines/InventoryLines";
import type { InventoryItem } from "../../models/InventoryItem";
import { useAuth } from "../../auth/useAuth";
export default function InventoryCountHTML() {
  const [revision,setRevision]=useState(0);
  const { Warehouse } = useWarehouse();
  const { products, warehouses, error, loading } = useInventoryLookups();
  const [stock, setStock] = useState<InventoryItem[]>([]);
  const [stockError, setStockError] = useState("");
  const [stockLoading, setStockLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setStockLoading(true);setStockError("");setStock([]);
    void loadAllLookupPages(InventoryService(Warehouse?.Code).GetAll)
      .then((x) => {
        if (active) {
          setStock(x);
          setStockLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setStockError(
            "Unable to load system stock. Reload before entering a count.",
          );
          setStockLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [Warehouse?.Code, revision]);
  const { hasPermission } = useAuth();
  const manage =
    hasPermission("InventoryCount.Manage") || hasPermission("Inventory.Manage");
  return (
    <>
      <p role={error || stockError ? "alert" : "status"}>
        {error ||
          stockError ||
          (loading || stockLoading ? "Loading count options…" : "")}
      </p>
      <ManagementPageHTML<InventoryCount> key={`${Warehouse?.Code}:${revision}`} RenderRowActions={row=>!row.IsCompleted && hasPermission("InventoryCount.Post") ? <PostStockButton kind="count" code={row.Code} onPosted={()=>setRevision(v=>v+1)}/>:null}
        DialogSections={[{Title:"Count details",Keys:["Code","WareHouse","Status","Comment"]}]}
        DialogSummary={v=>{const lines=(v.Items??[]) as InventoryCountItem[];return <div className="editor-summary"><h3>Count summary</h3><div><span>Products</span><strong>{lines.length}</strong></div><div><span>Lines with variance</span><strong>{lines.filter(x=>x.QuantityCounted!==x.QuantityAvailable).length}</strong></div><div><span>Matching lines</span><strong>{lines.filter(x=>x.QuantityCounted===x.QuantityAvailable).length}</strong></div><p>Review differences in each product’s base unit. Save the count, then use Post to stock to reconcile differences. Posted counts are locked.</p></div>;}}
        Title="Stock counts"
        Eyebrow="Stock verification"
        Description="Capture physical counts by warehouse and review differences from available inventory."
        AddLabel="Start count"
        SearchPlaceholder="Search count code or comment"
        Rows={[]}
        Summaries={[]}
        CanExport={false}
        Service={InventoryCountService(Warehouse?.Code)}
        CanCreate={manage && !loading && !stockLoading && !error && !stockError}
        CanEdit={manage && !loading && !stockLoading && !error && !stockError}
        CanDelete={manage}
        CanEditRow={row=>!row.IsCompleted}
        CanDeleteRow={row=>!row.IsCompleted}
        DefaultValues={{
          Items: [],
          Status: Status.Active,
          WareHouse: Warehouse?.Code,
        }}
        Statuses={Object.values(Status)}
        GetStatus={(x) => x.Status}
        GetRowKey={(x) => x.Code}
        GetSearchText={(x) => `${x.Code} ${x.WareHouse} ${x.Comment ?? ""}`}
        Columns={[{Key:"Posting",Header:"Posting",Render:row=>row.IsCompleted?"Posted · locked":"Draft"},
          {
            Key: "Code",
            Header: "Count",
            Render: (x) => (
              <IdentityCell
                Icon="fa-clipboard-check"
                Title={x.Code}
                Subtitle={x.Comment ?? ""}
              />
            ),
          },
          {
            Key: "Warehouse",
            Header: "Warehouse",
            Render: (x) =>
              <EntityLabel entity="warehouse" code={x.WareHouse}/>,
          },
          {
            Key: "Items",
            Header: "Products",
            Render: (x) => (
              <details>
                <summary>View {x.Items?.length ?? 0} lines</summary>
                <ul>
                  {(x.Items ?? []).map((l, i) => (
                    <li key={i}>
                      <EntityLabel entity="product" code={l.ProductCode}/>{" "}
                      · system {l.QuantityAvailable} · counted{" "}
                      {l.QuantityCounted} · variance{" "}
                      {l.QuantityCounted - l.QuantityAvailable} ·{" "}
                      {l.Reason || "No reason"}
                    </li>
                  ))}
                </ul>
              </details>
            ),
          },
          {
            Key: "Variance",
            Header: "Lines with variance",
            Render: (x) =>
              (x.Items ?? []).filter(l=>l.QuantityCounted !== l.QuantityAvailable).length,
          },
          {
            Key: "Status",
            Header: "Status",
            Render: (x) => <StatusBadge Status={x.Status} />,
          },
        ]}
        DialogFields={[
          { Key: "Code", Label: "Count code", Disable: true },
          {
            Key: "WareHouse",
            Disable: true,
            Label: "Warehouse",
            Required: true,
            Type: "select",
            Options: warehouses.map((x) => ({
              display: String(x.Value),
              value: x.Key,
              other: null,
            })),
          },
          {
            Key: "Comment",
            Label: "Comment",
            Type: "textarea",
            FullWidth: true,
          },
          {
            Key: "Status",
            Label: "Status",
            Required: true,
            Type: "select",
            Options: Object.values(Status),
          },
        ]}
        OnFormValueChange={(field, value, values) =>
          field === "WareHouse"
            ? {
                Items: ((values.Items as InventoryCountItem[]) ?? []).map(
                  (x) => ({
                    ...x,
                    QuantityAvailable: available(
                      x.ProductCode,
                      String(value),
                      stock,
                    ),
                  }),
                ),
              }
            : undefined
        }
        ValidateForm={(v): Record<string, string> => {
          const msg = validateInventoryLines(v.Items, false);
          return msg ? { Items: msg } : {};
        }}
        RenderDialogContent={({
          FormValues,
          SetFormValue,
          ValidationErrors,
        }) => (
          <>
            <CountLines
              items={(FormValues.Items ?? []) as InventoryCountItem[]}
              products={products}
              onChange={(items) => {
                const previous = (FormValues.Items ??
                  []) as InventoryCountItem[];
                SetFormValue(
                  "Items",
                  items.map((x, i) =>
                    previous.some((p) => p.ProductCode === x.ProductCode)
                      ? x
                      : {
                          ...x,
                          QuantityAvailable: available(
                            x.ProductCode,
                            String(FormValues.WareHouse ?? ""),
                            stock,
                          ),
                        },
                  ),
                );
              }}
            />
            {ValidationErrors.Items && (
              <p role="alert">{ValidationErrors.Items}</p>
            )}
          </>
        )}
        GetSummaries={(rows, total) => [
          {
            Label: "Counts",
            Value: total,
            Icon: "fa-clipboard-check",
            Tone: "Blue",
          },
          {
            Label: "Lines with variance on this page",
            Value: rows
              .flatMap((x) => x.Items ?? [])
              .filter((x) => x.QuantityAvailable !== x.QuantityCounted).length,
            Icon: "fa-triangle-exclamation",
            Tone: "Orange",
          },
        ]}
      />
    </>
  );
}
function available(
  product: string,
  warehouse: string,
  rows: InventoryItem[],
): number {
  return rows
    .filter((x) => x.ProductCode === product && x.WareHouse === warehouse)
    .reduce((sum, x) => sum + x.Quantity - x.ReservedQuantity, 0);
}
